import { useState, useMemo, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { useRecoverRequestsByObjectID } from '../hooks'
import { DataTable } from '@/components/data/DataTable'
import { ColumnDef } from '@tanstack/react-table'
import type { RecoverRequestDetail } from '../api'
import { FileJson } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { JsonViewDialog } from '@/components/ui/JsonViewDialog'
import { formatBytes } from '@/features/objects/utils/formatSize'

import { AnalyticsStatusIndicator } from '@/lib/utils/analytics-status'

const getStatusCircle = (analytics: number | null) => (
    <AnalyticsStatusIndicator status={analytics} size="h-4 w-4" />
)

interface RecoverRequestDetailsDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    objectID: string | null
    requestID: string | null
}

export function RecoverRequestDetailsDialog({
    open,
    onOpenChange,
    objectID,
    requestID,
}: RecoverRequestDetailsDialogProps) {
    const { t } = useTranslation()
    const { data: requests, isLoading } = useRecoverRequestsByObjectID(objectID, requestID)
    const [jsonDialog, setJsonDialog] = useState<{ title: string; content: string | null } | null>(null)

    const handleShowMetadata = useCallback((title: string, content: string | null) => {
        setJsonDialog({ title, content })
    }, [])

    const columns: ColumnDef<RecoverRequestDetail>[] = useMemo(() => [
        {
            accessorKey: 'tier',
            header: t('recoverRequests.tier'),
            cell: ({ row }) => {
                const tier = row.getValue('tier') as string | null
                return <span className="text-sm">{tier || '-'}</span>
            },
        },
        {
            accessorKey: 'versionAnalytics',
            header: () => <div className="text-center w-full">{t('recoverRequests.analytics')}</div>,
            cell: ({ row }) => {
                const analytics = row.getValue('versionAnalytics') as number | null
                return (
                    <div className="flex items-center justify-center">
                        {getStatusCircle(analytics)}
                    </div>
                )
            },
        },
        {
            accessorKey: 'versionSize',
            header: () => <div className="text-right w-full">{t('recoverRequests.size')}</div>,
            cell: ({ row }) => {
                const size = row.getValue('versionSize') as number | null
                if (size === null || size === undefined) return <div className="text-right text-muted-foreground">-</div>
                return (
                    <div className="text-right">
                        <span className="text-sm whitespace-nowrap">
                            {formatBytes(size)}
                        </span>
                    </div>
                )
            },
        },
        {
            accessorKey: 'versionMetadata',
            header: t('recoverRequests.metadata'),
            cell: ({ row }) => {
                const metadata = row.getValue('versionMetadata') as string | null
                if (!metadata) return <span className="text-muted-foreground">-</span>
                return (
                    <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 px-1"
                        onClick={() => handleShowMetadata(t('recoverRequests.metadata'), metadata)}
                    >
                        <FileJson className="h-3.5 w-3.5" />
                    </Button>
                )
            },
        },
    ], [t, handleShowMetadata])

    return (
        <>
            <Dialog open={open} onOpenChange={onOpenChange}>
                <DialogContent className="sm:max-w-[1000px] max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>
                            {t('recoverRequests.objectDetails')} - {objectID}
                        </DialogTitle>
                        <DialogDescription>
                            {t('recoverRequests.objectDetailsDescription')}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="mt-4">
                        {isLoading ? (
                            <div className="flex items-center justify-center h-32">
                                <div className="text-muted-foreground">{t('recoverRequests.loading')}</div>
                            </div>
                        ) : !requests || requests.length === 0 ? (
                            <div className="flex items-center justify-center h-32">
                                <div className="text-muted-foreground">{t('recoverRequests.noRequests')}</div>
                            </div>
                        ) : (
                            <DataTable
                                columns={columns}
                                data={requests}
                                enableRowSelection={false}
                                hideSearch={true}
                            />
                        )}
                    </div>
                </DialogContent>
            </Dialog>

            {jsonDialog && (
                <JsonViewDialog
                    open={!!jsonDialog}
                    onOpenChange={(open) => {
                        if (!open) setJsonDialog(null)
                    }}
                    title={jsonDialog.title}
                    content={jsonDialog.content}
                />
            )}
        </>
    )
}
